import { BottomSheet, Button, type ButtonProps, Typography } from '@s4l/ds'
import { IconPhone } from '@s4l/icons/IconPhone'
import React from 'react'
import { useS4LContext } from '@s4l/react-context'
import CallCenter from 'client/icons/CallCenter'
import Hiperlink from 'client/icons/Hiperlink'
import Time from 'client/icons/Time'
import { useDevice } from 'src/hooks/useDevice'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { type SectionType } from 'src/hooks/useKafkaEvent/constants'
import { useMarket } from 'src/hooks/useMarket'
import { usePage } from 'src/hooks/usePage'
import { BreakpointSizes } from 'src/types/device'
import styles from './styles.module.css'

interface IHeaderDirectSales {
  readonly variant: 'pill' | 'text'
  section: SectionType
}

const HeaderDirectSales = ({ variant, section }: IHeaderDirectSales) => {
  const { i18n } = useS4LContext()
  const { helpCenterUrl, directSales } = useMarket()
  const { pageId } = usePage()
  const { activeBreakpoint } = useDevice()

  const { sendEvent: sendClickTel } = useKafkaEvent({ page: pageId, eventName: 'click_tel' })
  const { sendEvent: sendOpenDirectSales } = useKafkaEvent({
    page: `${pageId}`,
    eventName: 'click_open_direct_sales',
  })

  const { sendEvent: sendClickHelpCenter } = useKafkaEvent({
    page: pageId,
    eventName: 'click_help_center',
  })

  const isDesktop = activeBreakpoint >= BreakpointSizes.L

  function onOpenChange(open: boolean) {
    if (open) {
      sendOpenDirectSales({ section })
    }
  }

  const dayOfTheWeek = new Date().getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6

  let variantButton: ButtonProps['variant'] = isDesktop ? 'outlined-rounded' : 'icon-rounded'

  if (variant === 'text') {
    variantButton = 'text-flat'
  }

  return (
    <BottomSheet onOpenChange={onOpenChange}>
      <BottomSheet.Trigger asChild>
        <Button
          variant={variantButton}
          size="small"
          leadingIcon={
            variant === 'text' ? (
              <IconPhone key="text" size={16} data-testid="PhoneIcon" />
            ) : (
              <>
                <CallCenter className="desktop:display-none" />
                <IconPhone size={16} className="display-none desktop:display-block" />
              </>
            )
          }
        >
          <div className={styles.triggerWrapper}>
            {variant === 'pill' && (
              <div className="display-none desktop:display-block">
                <Typography as="p" bold variant="caption" size="l">
                  <Typography as="span" bold variant="caption" size="l">
                    {i18n.formatMessage({ id: 'global.call-to-book' })}
                  </Typography>
                  &nbsp; &#183; &nbsp;
                  <Typography as="span" bold variant="caption" size="l">
                    {i18n.formatMessage({ id: 'global.free' })}
                  </Typography>
                </Typography>

                <Typography as="p" bold variant="caption" size="l" className="align-start">
                  {directSales?.phone}
                </Typography>
              </div>
            )}

            {variant === 'text' && (
              <Typography as="p" className="align-start" variant="body" size="m">
                {directSales?.phone}
              </Typography>
            )}
          </div>
        </Button>
      </BottomSheet.Trigger>

      <BottomSheet.Content
        size="s"
        title={i18n.formatMessage({ id: 'direct-sales.header' })}
        modalOnDesktop
      >
        <div className={styles.directSalesContent}>
          <Typography as="h4" variant="title" size="xs">
            {i18n.formatMessage({ id: 'direct-sales.title' })}
          </Typography>
          <ul className={styles.list}>
            <Typography as="li" variant="body" size="l">
              <Button
                as="a"
                variant="text-flat"
                onClick={() => {
                  sendClickTel({ section })
                }}
                leadingIcon={<IconPhone />}
                href={`tel:${directSales?.phone}`}
              >
                {directSales?.phone}
              </Button>
              <Typography as="span" variant="body" size="s">
                {i18n.formatMessage({ id: 'direct-sales.phone-inquires' })}
              </Typography>
            </Typography>

            <Typography as="li" variant="body" size="l">
              <strong>
                <Time />
                {i18n.formatMessage({ id: 'direct-sales.working-hours-title' })}
              </strong>
              <span>{directSales?.schedule[dayOfTheWeek]} </span>
            </Typography>
          </ul>

          <Button
            fullWidth
            variant="filled-rounded"
            leadingIcon={<IconPhone />}
            onClick={() => {
              sendClickTel({ section })
              window.location.href = `tel:${directSales?.phone}`
            }}
          >
            {i18n.formatMessage({ id: 'direct-sales.cta' })}
          </Button>

          <Typography as="p" variant="body" size="l" className="align-center">
            {i18n.formatMessage({ id: 'direct-sales.assistance' })}
          </Typography>

          <Button
            variant="text-flat"
            as="a"
            onClick={() => sendClickHelpCenter({ section })}
            href={helpCenterUrl}
            target="_blank"
            trailingIcon={<Hiperlink />}
            className={['ml-auto', 'mr-auto']}
          >
            {i18n.formatMessage(
              { id: 'direct-sales.cta-help-center' },
              {
                helpCenter: i18n.formatMessage({ id: 'global.help-center-full' }),
              }
            )}
          </Button>
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  )
}

export default HeaderDirectSales
